import { useAgentChat, type AgentChat } from "./useAgentChat";
import React, {
  createContext,
  FC,
  ReactNode,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";

type AgentContextValue = {
  /** The live conversation. Held here, not in the panel - see below. */
  chat: AgentChat;
  isOpen: boolean;
  open: () => void;
  close: () => void;
  toggle: () => void;
  /** Seed text dropped into the composer when the panel opens from a prompt chip. */
  seed: string | null;
  openWith: (prompt: string) => void;
  consumeSeed: () => void;
};

const AgentContext = createContext<AgentContextValue | null>(null);

/**
 * Owns the agent panel's visibility AND the conversation itself.
 *
 * The conversation used to live inside the panel component. Gatsby unmounts the
 * whole page tree on a client-side navigation, so opening the agent, asking a
 * question and then clicking a nav link destroyed the messages, the session and
 * the in-flight stream. Mounted from `wrapRootElement` instead, this provider
 * sits above the router and survives navigation.
 *
 * The panel below it may still unmount and remount freely; it reads the
 * conversation from here rather than owning it.
 */
export const AgentProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [seed, setSeed] = useState<string | null>(null);
  const chat = useAgentChat();

  const open = useCallback(() => setIsOpen(true), []);
  const close = useCallback(() => setIsOpen(false), []);
  const toggle = useCallback(() => setIsOpen((v) => !v), []);
  const openWith = useCallback((prompt: string) => {
    setSeed(prompt);
    setIsOpen(true);
  }, []);
  const consumeSeed = useCallback(() => setSeed(null), []);

  const value = useMemo(
    () => ({ chat, isOpen, open, close, toggle, seed, openWith, consumeSeed }),
    [chat, isOpen, open, close, toggle, seed, openWith, consumeSeed]
  );

  return <AgentContext.Provider value={value}>{children}</AgentContext.Provider>;
};

export const useAgent = (): AgentContextValue => {
  const ctx = useContext(AgentContext);
  if (!ctx) throw new Error("useAgent must be used within an AgentProvider");
  return ctx;
};
